import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, User } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const sections = [
  { label: "Peptides", id: "products" },
  { label: "Why Us", id: "why-us" },
  { label: "Manifesto", id: "manifesto" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { count, setOpen: setCartOpen } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-[100] transition-[background-color,border-color] duration-500 ${
        scrolled || location.pathname !== "/"
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="logo-home-button"
          onClick={() => navigate("/")}
          className="font-display text-3xl md:text-4xl tracking-[0.15em] text-[#E6DFD4] leading-none"
        >
          SANDFORM
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {sections.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => goSection(l.id)}
              className="text-xs tracking-[0.22em] uppercase font-bold text-[#A3A3A3] hover:text-[#E6DFD4] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}

          <button
            data-testid="header-account-button"
            onClick={() => navigate(user ? "/account" : "/login")}
            className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-bold text-[#A3A3A3] hover:text-[#E6DFD4] transition-colors duration-300"
          >
            <User size={16} />
            {user ? "Account" : "Login"}
          </button>

          <button
            data-testid="header-cart-button"
            onClick={() => setCartOpen(true)}
            className="relative flex items-center gap-2 bg-[#E6DFD4] text-black px-5 py-3 text-xs tracking-[0.18em] uppercase font-bold hover:bg-white transition-colors duration-300"
          >
            <ShoppingBag size={16} />
            Cart
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 h-5 min-w-5 px-1 flex items-center justify-center rounded-full bg-black text-[#E6DFD4] text-[10px] font-bold">
                {count}
              </span>
            )}
          </button>
        </nav>

        <div className="flex items-center gap-4 md:hidden">
          <button
            data-testid="header-cart-button-mobile"
            onClick={() => setCartOpen(true)}
            className="relative text-[#E6DFD4]"
            aria-label="Cart"
          >
            <ShoppingBag size={24} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 h-5 min-w-5 px-1 flex items-center justify-center rounded-full bg-[#E6DFD4] text-black text-[10px] font-bold">
                {count}
              </span>
            )}
          </button>
          <button onClick={() => setOpen((v) => !v)} className="text-[#E6DFD4]" aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {sections.map((l) => (
                <button key={l.id} onClick={() => goSection(l.id)} className="text-left font-display text-3xl tracking-wide text-[#F5F5F5]">
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => { setOpen(false); navigate(user ? "/account" : "/login"); }}
                className="text-left font-display text-3xl tracking-wide text-[#F5F5F5]"
              >
                {user ? "Account" : "Login"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
